import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map, Observable, Subject, Subscription, switchMap } from 'rxjs';
import { first, tap, withLatestFrom } from 'rxjs/operators';
import { isNotNullOrUndefined } from '../../helper/helper';

export interface PaginationDetails<TFilter = {}, TSort = string> {
    offset: number;
    pageSize: number;
    searchQuery?: string;
    sortDirection?: 'asc' | 'desc';
    sortProperty?: TSort;
    filterObject?: TFilter;
}

export interface PageContent<TItem> {
    items: TItem[];
    length: number;
}
export interface StateStatus {
    isLoading: boolean;
    hasError: boolean;
    isCountLimitReached: boolean;
}

export interface PaginatedItemsState<TItem, TFilter = {}, TSort = string> {
    paginationDetails: PaginationDetails<TFilter, TSort>;
    pageContent: PageContent<TItem>;
    status: StateStatus;
}
@Injectable()
export abstract class PaginatedItemsService<TItem, TFilter = {}, TSort = string> implements OnDestroy {
    private state = new BehaviorSubject<PaginatedItemsState<TItem, TFilter, TSort>>(null);
    protected abstract getItems(paginationDetails: PaginationDetails<TFilter, TSort>): Observable<Pick<PageContent<TItem>, 'items' | 'length'> & Pick<StateStatus, 'hasError' | 'isCountLimitReached'>>;

    readonly selectPaginatedItemsState$ = this.state.pipe(map((state) => state));

    readonly selectPaginationDetails$ = this.selectPaginatedItemsState$.pipe(map(({ paginationDetails }) => paginationDetails));
    readonly selectOffset$ = this.selectPaginationDetails$.pipe(map(({ offset }) => offset));
    readonly selectPageSize$ = this.selectPaginationDetails$.pipe(map(({ pageSize }) => pageSize));
    readonly selectSearchQuery$ = this.selectPaginationDetails$.pipe(map(({ searchQuery }) => searchQuery));
    readonly selectFilterObject$ = this.selectPaginationDetails$.pipe(map(({ filterObject }) => filterObject));

    readonly selectPageContent$ = this.selectPaginatedItemsState$.pipe(map(({ pageContent }) => pageContent));
    readonly selectItems$ = this.selectPageContent$.pipe(map(({ items }) => items));
    readonly selectLength$ = this.selectPageContent$.pipe(map(({ length }) => length));

    readonly selectStatus$ = this.selectPaginatedItemsState$.pipe(map(({ status }) => status));
    readonly selectIsLoading$ = this.selectStatus$.pipe(map(({ isLoading }) => isLoading));
    readonly selectIsCountLimitReached$ = this.selectStatus$.pipe(map(({ isCountLimitReached }) => isCountLimitReached));
    private loadPageAction$ = new Subject<Partial<PaginationDetails<TFilter, TSort>> | void>();
    private loadPageSubscription: Subscription;

    constructor(initialState: PaginatedItemsState<TItem, TFilter, TSort>) {
        this.state.next(initialState);
        this.loadPageSubscription = this.loadPageAction$
            .pipe(
                withLatestFrom(this.selectPaginationDetails$),
                switchMap(([paginationDetailsOverrides, paginationDetails]) => {
                    this.state.next({ ...this.state.value, status: { ...this.state.value.status, isLoading: true } });

                    let p = { ...paginationDetails };
                    if (paginationDetailsOverrides) {
                        p = { ...paginationDetails, ...paginationDetailsOverrides };
                        this.updatePagination(p);
                    }
                    return this.getItems(p)
                }),
                tap(({ items, length, hasError, isCountLimitReached }) => {
                    this.updatePaginatedItems({ items, length });
                    this.state.next({ ...this.state.value, status: { ...this.state.value.status, isLoading: false, hasError, isCountLimitReached } });
                }),
            )
            .subscribe();
    }

    readonly setSort = async (sort: Pick<PaginationDetails<TFilter, TSort>, 'sortDirection' | 'sortProperty'>) => {
        const pagination = await firstValueFrom(this.selectPaginationDetails$);
        this.updatePagination({ ...pagination, ...sort });
    };

    readonly setSearch = async (searchQuery: PaginationDetails<TFilter, TSort>['searchQuery']) => {
        const pagination = await firstValueFrom(this.selectPaginationDetails$);
        this.updatePagination({ ...pagination, searchQuery });
    };
    readonly setFilter = async (filterObject: PaginationDetails<TFilter, TSort>['filterObject']) => {
        const pagination = await firstValueFrom(this.selectPaginationDetails$);
        this.updatePagination({ ...pagination, filterObject });
    };

    readonly loadPage = (paginationDetailsOverrides?: Partial<PaginationDetails<TFilter, TSort>>) => {
        this.loadPageAction$.next(paginationDetailsOverrides);
    };
    readonly loadNextPage = () => {
        return this.selectPaginationDetails$
            .pipe(
                first(),
                tap(({ offset, pageSize }) =>
                    this.updatePagination({
                        offset: offset + pageSize,
                        pageSize,
                    }),
                ),
                tap(() => this.loadPage()),
            )
            .subscribe();
    };

    private readonly updatePaginatedItems = (pageContent: PageContent<TItem>) => {
        // backends schicken length nur bei offset 0 mit
        let length = this.state.value.pageContent.length;
        if (isNotNullOrUndefined(pageContent.length)) {
            length = pageContent.length;
        }
        this.state.next({
            ...this.state.value,
            pageContent: {
                ...pageContent,
                length,
            },
        });
    };
    private readonly updatePagination = (paginationDetails: PaginationDetails<TFilter, TSort>) => {
        this.state.next({
            ...this.state.value,
            paginationDetails,
        });
    };
    ngOnDestroy() {
        this.loadPageSubscription?.unsubscribe();
    }
}
